/*---------------------------------------------------------------
 ?? Project: Ring2All Platform
 ????? Author: Rodrigo Cuadra
 ?? Company: Ring2All LLC
 ?? File: NetworkSettingsPage.tsx
 ??? Created: 2025-12-04
 ?? Description:
    Network Configuration page - Lists Telephony Servers
    and allows editing their network configuration
----------------------------------------------------------------*/

import { useState, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Network } from 'lucide-react';
import { useI18n } from '../../hooks/useI18n';
import { useNotifications } from '../../contexts/NotificationContext';
import DataGridSimple from '../../components/ui/DataGridSimple';
import { ModulePageContainer } from '../../components/layouts';
import { useTelephonyServersList } from './hooks/useTelephonyServersList';
import { NetworkActions } from './components/NetworkActions';
import type { TelephonyServerWithNetwork } from './types';

export default function NetworkSettingsPage() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const { showSuccess } = useNotifications();

  const [refreshingId, setRefreshingId] = useState<string | null>(null);
  const [networkInfo, setNetworkInfo] = useState<Record<string, { hostname: string; ipAddress: string; gateway: string; primaryDns: string }>>({});

  const {
    servers,
    loading,
    columns,
    pagination,
    searchQuery,
    sortBy,
    sortOrder,
    handleSearch,
    handleSort,
    handlePageChange,
    handleItemsPerPageChange,
    handleColumnToggle,
    refresh: refreshServersList,
  } = useTelephonyServersList();

  const handleEditNetwork = useCallback(
    (server: TelephonyServerWithNetwork) => {
      navigate(`/admin/network/settings/${server.uuid ?? server.id}`);
    },
    [navigate]
  );

  const handleRefreshNetwork = useCallback(
    async (server: TelephonyServerWithNetwork) => {
      setRefreshingId(String(server.id));
      try {
        // Import the API function dynamically to avoid circular dependencies
        const { fetchServerNetworkConfig } = await import('./api/networkSettingApi');
        
        // Fetch the network configuration from the server
        // This also triggers server status verification on the backend
        const networkConfig = await fetchServerNetworkConfig(server.id);
        
        // Extract info from the first interface
        const primaryInterface = networkConfig.interfaces[0];
        if (primaryInterface) {
          // Update local state with network info
          setNetworkInfo(prev => ({
            ...prev,
            [server.id]: {
              hostname: primaryInterface.hostname || '-',
              ipAddress: primaryInterface.ipAddress,
              gateway: primaryInterface.gateway,
              primaryDns: primaryInterface.primaryDns,
            }
          }));
          showSuccess(t('networkSettings.messages.refreshSuccess'));
        }
        
        // Refresh the entire servers list to get updated status
        await refreshServersList();
      } catch (error) {
        console.error('Error refreshing network info:', error);
        showSuccess(t('networkSettings.messages.refreshSuccess'));
      } finally {
        setRefreshingId(null);
      }
    },
    [showSuccess, t, refreshServersList]
  );

  const renderActions = useCallback(
    (server: TelephonyServerWithNetwork) => (
      <NetworkActions
        server={server}
        refreshingId={refreshingId}
        onEdit={handleEditNetwork}
        onRefresh={handleRefreshNetwork}
      />
    ),
    [handleEditNetwork, handleRefreshNetwork, refreshingId]
  );

  // Merge servers with cached network info
  const serversWithNetworkInfo = useMemo(() => {
    return servers.map(server => ({
      ...server,
      ...(networkInfo[server.id] || {}),
    }));
  }, [servers, networkInfo]);

  return (
    <ModulePageContainer
      currentView="list"
      listView={
        <DataGridSimple
          title={t('networkSettings.title')}
          titleIcon={<Network className="h-5 w-5 text-primary" />}
          data={serversWithNetworkInfo}
          columns={columns}
          loading={loading}
          pagination={pagination}
          searchQuery={searchQuery}
          sortBy={sortBy}
          sortOrder={sortOrder}
          onSort={handleSort}
          onSearch={handleSearch}
          onColumnToggle={handleColumnToggle}
          onPageChange={handlePageChange}
          onItemsPerPageChange={handleItemsPerPageChange}
          addButtonText={undefined}
          emptyMessage={t('networkSettings.noServersFound')}
          searchPlaceholder={t('networkSettings.searchServers')}
          renderActions={renderActions}
          translations={{
            items: t('common.items'),
            columns: t('common.columns'),
            noItemsFound: t('common.noItemsFound'),
            showing: t('common.showing'),
            to: t('common.to'),
            of: t('common.of'),
            results: t('common.results'),
            previous: t('common.previous'),
            next: t('common.next'),
            page: t('common.page'),
            deleteItem: t('common.deleteItem'),
            areYouSureDeleteItem: t('common.areYouSureDeleteItem'),
            delete: t('common.delete'),
            cancel: t('common.cancel'),
            allSelected: t('common.allSelected'),
            selected: t('common.selected'),
            searchOptions: t('common.searchOptions'),
            noOptionsFound: t('common.noOptionsFound'),
            selectAll: t('common.selectAll'),
            clearAll: t('common.clearAll')
          }}
        />
      }
      formView={null}
    />
  );
}
